import { useEffect } from 'react';
import { billingService } from '@/services/billingService';
import { isNativeApp } from '@/utils/platform';

/**
 * Keeps the server's record of App Store purchases in step with StoreKit's,
 * for the life of the app.
 *
 * Two things, both native-only and both no-ops until the account has bought
 * something:
 *
 *  - Attach to the transactionUpdated stream once. The native listener has
 *    been running since launch and holds anything that arrived before the web
 *    view was ready — a purchase that completed while the app was killed, a
 *    renewal, a refund, an Ask to Buy approval. Each is recorded with the
 *    server and only then finished (see billingService).
 *
 *  - On sign-in, record whatever StoreKit says is active now, so a reinstall
 *    or a new device resolves without the user tapping Restore.
 *
 * `onRecorded` fires after any transaction the server records, from either
 * path or from a purchase, so the caller can refresh its view of the account.
 */
export const useAppStoreBilling = (isAuthenticated: boolean, onRecorded?: () => void): void => {
  useEffect(() => {
    if (!isNativeApp()) return;
    void billingService.startListening().catch((error) => {
      console.warn('App Store transaction listener did not attach:', error);
    });
  }, []);

  useEffect(() => {
    if (!isNativeApp() || !isAuthenticated) return;
    void billingService.syncEntitlements().catch((error) => {
      console.warn('App Store entitlement sync failed:', error);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isNativeApp() || !onRecorded) return;
    return billingService.onRecorded(onRecorded);
  }, [onRecorded]);
};

export default useAppStoreBilling;
